import { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error | undefined;
  errorInfo?: ErrorInfo | undefined;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    this.setState({
      error,
      errorInfo,
    });

    // Log error to monitoring service in production
    if (process.env.NODE_ENV === 'production') {
      // TODO: Implement error logging service
      console.error('Production error:', {
        error: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
      });
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false });
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className='min-h-screen flex items-center justify-center bg-gray-50'>
          <div className='max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center'>
            <div className='flex justify-center mb-4'>
              <AlertTriangle className='w-16 h-16 text-red-500' />
            </div>

            <h1 className='text-2xl font-bold text-gray-900 mb-2'>
              Something went wrong
            </h1>

            <p className='text-gray-600 mb-6'>
              We&apos;re sorry, but something unexpected happened. Please try
              refreshing the page or go back to the home page.
            </p>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className='mb-6 text-left'>
                <summary className='cursor-pointer text-sm font-medium text-gray-700 mb-2'>
                  Error Details (Development Only)
                </summary>
                <div className='bg-gray-100 p-3 rounded text-xs text-gray-600 overflow-auto max-h-32'>
                  <div className='font-mono'>
                    <div className='font-bold'>Error:</div>
                    <div className='mb-2'>{this.state.error.message}</div>
                    <div className='font-bold'>Stack:</div>
                    <div className='whitespace-pre-wrap'>
                      {this.state.error.stack}
                    </div>
                  </div>
                </div>
              </details>
            )}

            <div className='flex space-x-3 justify-center'>
              <button
                onClick={this.handleRetry}
                className='flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors'
              >
                <RefreshCw className='w-4 h-4 mr-2' />
                Try Again
              </button>

              <button
                onClick={this.handleGoHome}
                className='flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors'
              >
                <Home className='w-4 h-4 mr-2' />
                Go Home
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
